import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUser } from '@clerk/react';
import {
  trackScanStarted,
  trackScanComplete,
  trackScanError,
  watchScan,
  getScanIntentId,
  getScanIntentToken,
  clearScanIntentId,
  clearScanIntentToken,
  setScanIntentId,
  setScanIntentToken,
  getUnlockIntent,
  setUnlockIntent,
  clearUnlockIntent,
  trackInitiateCheckout,
  readMetaAttribution,
} from '@privisy/ui-bridge';
import ScannerDashboardContent from './components/ScannerDashboardContent';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import SignedOutRedirect from './components/SignedOutRedirect';
import { navigation } from './utils/signInRedirect';
import { freeAuditUrl } from './utils/freeAudit';
import AnonymousClaimHandoff from './components/AnonymousClaimHandoff';
import ScanResultRoute from './components/ScanResultRoute';
import { renderSecondaryPagesRoute } from './components/SecondaryPagesRoute';
import { renderStandalonePagesRoute } from './components/StandalonePagesRoute';
import { renderRouteLoadingRoute } from './components/RouteLoadingRoute';
import { ScanResult, ReportRecord, ReportKind } from './utils/types';
import { API_BASE, startScan, getCreditInfo, getScans, getScan, getReport, getReportSummary, createUnlockCheckout, ScanProgress as ScanProgressType, ScanSectionOptions, mergeProgressFrame } from './utils/api';
import { billingRedirect } from './utils/billing';
import type { SignedOutUnlock } from './components/PaywallOffer';
import { ApiError, isInsufficientCredits } from './utils/api-error';
import { useCreditInfo } from './hooks/useCreditInfo';
import { useUnlockReturn } from './hooks/useUnlockReturn';
import { useQueueCount } from './hooks/useQueueCount';
import { useClaimScan, resolveClaimIdentity } from './hooks/useClaimScan';
import { resolveRoute, parseParams, PageKey, PaymentMode, NavPage } from './utils/routes';
import { T } from './designTokens';

interface AppProps {
  authEnabled: boolean;
  getToken?: () => Promise<string | null>;
  clerkSignedIn?: boolean;
  isAdmin?: boolean;
}

// The #/scan/:id and #/report/:id routing effects both skip re-fetching when
// the currently-loaded result already matches the route (unless it's locked,
// since "Run full audit" needs the stale locked result replaced once the
// fresh scan lands) — issue #770. Takes the two fields rather than the whole
// result so callers keep passing `currentResult?.id` / `?.licenseStatus`
// member expressions into the effect body — the shape react-hooks/exhaustive-deps
// already scopes the effect's dependency array to.
function isRouteResultFresh(
  currentId: string | undefined,
  currentLicenseStatus: ScanResult['licenseStatus'] | undefined,
  targetId: string
): boolean {
  return currentId === targetId && currentLicenseStatus !== 'locked';
}

// A report's scan_data blob is the snapshot taken when the scan finished, so
// its licenseStatus still reads 'locked' after a full audit is unlocked in
// place (#1762). The row's own column is authoritative, as it is on the
// backend's scan reads (scanFromReport).
function resultFromReport(report: ReportRecord): ScanResult {
  return report.licenseStatus
    ? { ...report.scanData, licenseStatus: report.licenseStatus }
    : report.scanData;
}

// The cross-subdomain cookie fallback (issue #870) persists only scanId +
// token, never domain — so a claim identity resolved from cookies has no
// domain to show. Once the background scan-route poll lands `currentResult`,
// derive it from the scanned URL instead of leaving the pitch generic for
// the rest of the session.
function hostnameFromUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

// The scan lane behind a report (#1760), read from its summary: a scan
// result carries no lane of its own. A locked report's summary is readable
// without an account, so this serves the signed-out token holder too. null
// when the report has no id, the lane is unknown, or the read fails, which
// every caller treats as "not a full audit", the legacy path.
async function readReportKind(reportId: string | null | undefined): Promise<ReportKind | null> {
  if (!reportId) return null;
  try {
    return (await getReportSummary(reportId)).kind ?? null;
  } catch {
    return null;
  }
}

function App({ authEnabled, getToken, clerkSignedIn, isAdmin }: AppProps) {
  const isSignedIn = authEnabled ? !!clerkSignedIn : true;
  const [currentResult, setCurrentResult] = useState<ScanResult | null>(null);
  const [pastScans, setPastScans] = useState<ScanResult[]>([]);
  const [pastScansLoaded, setPastScansLoaded] = useState(false);
  // "Audit again" on a past site loads its URL into the audit form.
  const [auditPrefill, setAuditPrefill] = useState<{ url: string; nonce: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // insufficient_credits (402) gets PaywallOffer's out-of-credits offer
  // surface (issue #943) instead of the raw ApiError.message string (which
  // is the machine code — assertOk prefers body.error over body.message)
  // landing in the generic `error` string above. That machine code must
  // never reach the DOM (issue #874) — only the rendering mechanism moved
  // from ApiErrorNotice to PaywallOffer, the guarantee itself is unchanged.
  const [creditError, setCreditError] = useState<ApiError | null>(null);
  const [progress, setProgress] = useState<ScanProgressType | null>(null);
  const [currentPage, setCurrentPage] = useState<PageKey>('scanner');
  const [purchasedScanId, setPurchasedScanId] = useState<string | null>(null);
  // Whether that purchase unlocked the report (#1762), for the banner's copy.
  const [purchaseUnlockedReport, setPurchaseUnlockedReport] = useState(false);
  const [salesReportId, setSalesReportId] = useState<string | null>(null);
  const [salesReportToken, setSalesReportToken] = useState<string | null>(null);
  const [scanRouteId, setScanRouteId] = useState<string | null>(null);
  const [reportRouteId, setReportRouteId] = useState<string | null>(null);
  // The report id a route load adopted, pinned to the scan it belongs to.
  // A report's scan_data snapshot may carry no reportId of its own, so the
  // route's report.id has to be remembered, but only for that one result:
  // every other way a result reaches the screen (a past scan, a batch entry,
  // a fresh scan) replaces currentResult without touching this. Held on its
  // own, it outlived its result and the next site's report rendered, shared
  // and fetched proof-of-scan screenshots under the previous report's id.
  const [loadedReport, setLoadedReport] = useState<{ scanId: string; reportId: string | null } | null>(null);
  const currentReportId = currentResult
    ? (loadedReport?.scanId === currentResult.id ? loadedReport.reportId : null) ?? currentResult.reportId ?? null
    : null;
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const [routeProgress, setRouteProgress] = useState<ScanProgressType | null>(null);
  const [batchInfo, setBatchInfo] = useState<{ current: number; total: number; url: string } | null>(null);
  const [scanningUrl, setScanningUrl] = useState<string | null>(null);
  const [allBatchResults, setAllBatchResults] = useState<ScanResult[]>([]);
  const [batchResultIndex, setBatchResultIndex] = useState(0);
  const [paymentSessionId, setPaymentSessionId] = useState<string | null>(null);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('credits');
  const [paymentReportId, setPaymentReportId] = useState<string | null>(null);
  // An unlock checkout's return (#1776): #/scan/:id?session_id=… waits here
  // for the webhook's unlock (#1775) before the report is shown unlocked.
  const [unlockSessionId, setUnlockSessionId] = useState<string | null>(null);
  const [showPaymentBanner, setShowPaymentBanner] = useState(false);
  const [policyAnalyzerId, setPolicyAnalyzerId] = useState<string | null>(null);
  const [adminUserId, setAdminUserId] = useState<string | null>(null);
  // Issue #877: the raw error (if any) from the most recent single-URL
  // handleScan() attempt — set alongside the existing `error` string so a
  // 402 `insufficient_credits` failure can render through PaywallOffer's
  // out-of-credits offer surface (issue #943) instead of a plain string.

  const { user } = useUser();
  const creditInfo = useCreditInfo(authEnabled, isSignedIn);
  // creditInfo itself is a fresh object every render (useCreditInfo returns a
  // new literal each time); pulling its stable refetch out here lets
  // handleScan below depend on the function without inheriting that churn.
  const { refetch: refetchCreditInfo } = creditInfo;
  // The post-claim unlock checkout reads the latest answer without
  // re-creating its callback on every render (#1779).
  const creditInfoRef = useRef(creditInfo);
  creditInfoRef.current = creditInfo;
  const queueCount = useQueueCount(authEnabled, isSignedIn);

  const setHashRoute = useCallback((route: string) => {
    if (window.location.hash !== route) {
      window.location.hash = route;
    }
  }, []);

  // Single page→hash-route mapping for the Header's onNavigate, replacing
  // the same if/else chain that used to be redefined at every call site
  // (issue #741).
  const navigateTo = useCallback((page: NavPage) => {
    if (page === 'reports') setHashRoute('#/reports');
    else if (page === 'queue') setHashRoute('#/queue');
    else if (page === 'billing') setHashRoute('#/billing');
    else if (page === 'monitoring') setHashRoute('#/monitoring');
    else setHashRoute('#/scanner');
  }, [setHashRoute]);

  // Issue #877: useClaimScan's handleClaim calls this exactly once, right
  // where the claim-success transition happens, with the claimed scanId.
  // settleClaim isn't defined yet at this point in the component body (it
  // needs state declared further down), so the callback we hand to the hook
  // is a stable indirection through a ref, updated below once settleClaim
  // exists, rather than a second guard of its own.
  const settleClaimRef = useRef<(scanId: string) => void>(() => {});
  const handleClaimSuccess = useCallback((scanId: string) => {
    settleClaimRef.current(scanId);
  }, []);
  // #1779: the same indirection for a SCAN_ALREADY_CLAIMED answer.
  const resumeAlreadyClaimedRef = useRef<(scanId: string) => void>(() => {});
  const handleAlreadyClaimed = useCallback((scanId: string) => {
    resumeAlreadyClaimedRef.current(scanId);
  }, []);

  const { claimParams, isClaiming, claimResult, setClaimResult, clearClaimParams } = useClaimScan(isSignedIn, authEnabled, setHashRoute, handleClaimSuccess, handleAlreadyClaimed);

  // Both routing effects and the anon watchScan onComplete handler load a
  // fresh result the same way: replace the current result, adopt its report
  // id, and drop any stale batch-result state (issue #770). setState setters
  // are referentially stable, so this callback never changes identity.
  const applyLoadedResult = useCallback((result: ScanResult, reportId: string | null) => {
    setCurrentResult(result);
    setLoadedReport({ scanId: result.id, reportId });
    setAllBatchResults([]);
    setBatchResultIndex(0);
  }, []);

  useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash;
      // Clear stale claim result when navigating away from claim landing
      if (!parseParams(window.location.search).get('claimScanId')) {
        setClaimResult(null);
      }

      // Single declarative hash-route table (issue #416) replaces what used
      // to be an order-dependent if/else chain duplicated across this
      // effect — see utils/routes.ts for the pattern → state-patch mapping.
      const { patch, externalRedirect } = resolveRoute(hash);

      if (patch.salesReportId !== undefined) setSalesReportId(patch.salesReportId);
      if (patch.salesReportToken !== undefined) setSalesReportToken(patch.salesReportToken);
      if (patch.currentPage !== undefined) setCurrentPage(patch.currentPage);
      if (patch.policyAnalyzerId !== undefined) setPolicyAnalyzerId(patch.policyAnalyzerId);
      if (patch.adminUserId !== undefined) setAdminUserId(patch.adminUserId);
      if (patch.paymentSessionId !== undefined) setPaymentSessionId(patch.paymentSessionId);
      if (patch.paymentMode !== undefined) setPaymentMode(patch.paymentMode);
      if (patch.paymentReportId !== undefined) setPaymentReportId(patch.paymentReportId);
      if (patch.unlockSessionId !== undefined) setUnlockSessionId(patch.unlockSessionId);
      if (patch.scanRouteId !== undefined) setScanRouteId(patch.scanRouteId);
      if (patch.reportRouteId !== undefined) setReportRouteId(patch.reportRouteId);
      if (patch.showPaymentBanner !== undefined) setShowPaymentBanner(patch.showPaymentBanner);
      if (patch.clearResult) {
        setCurrentResult(null);
        setAllBatchResults([]);
        setBatchResultIndex(0);
      }

      if (externalRedirect) {
        window.open(
          `${process.env.REACT_APP_MARKETING_URL || 'https://getprivisy.com'}/${externalRedirect}`,
          '_blank',
          'noopener,noreferrer'
        );
        window.location.hash = '#/';
      }
    };

    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, [setClaimResult]);

  useEffect(() => {
    if (!scanRouteId) return;

    let canceled = false;
    let disposeWatch: (() => void) | null = null;

    const loadScanResult = async () => {
      // A scan is actively starting (e.g. "Run full audit" from a locked report):
      // don't clobber the in-progress state by re-fetching the old route's scan.
      if (isLoading) return;
      if (isRouteResultFresh(currentResult?.id, currentResult?.licenseStatus, scanRouteId)) return;

      setIsRouteLoading(true);
      setError(null);
      setRouteProgress(null);

      if (authEnabled && !isSignedIn) {
        if (currentResult?.id === scanRouteId) {
          setIsRouteLoading(false);
          return;
        }
        // Anonymous/signed-out path: no stream to attach to, so the shared
        // scan client runs in its poll-only transport. Cadence, clamp, stale
        // budget and failure copy all come from @privisy/ui-bridge — this used
        // to be a third hand-rolled poll variant (issue #740).
        disposeWatch = watchScan<ScanResult>({
          apiBase: API_BASE,
          scanId: scanRouteId,
          transport: 'poll',
          onProgress: (next) =>
            setRouteProgress((prev) => mergeProgressFrame(prev, next as ScanProgressType)),
          onComplete: (terminal) => {
            if (terminal.outcome === 'delivered') {
              applyLoadedResult(terminal.result, terminal.result.reportId ?? null);
            } else {
              setCurrentResult(null);
              setLoadedReport(null);
            }
            setRouteProgress(null);
            setIsRouteLoading(false);
          },
          onFailure: (failure) => {
            setCurrentResult(null);
            setLoadedReport(null);
            setRouteProgress(null);
            // Friendly, categorized copy — signed-out visitors used to be shown
            // the backend's raw failure string here.
            setError(failure.message);
            setIsRouteLoading(false);
          },
        });
      } else {
        // Authenticated/default path
        try {
          const scan = await getScan(scanRouteId, getToken);
          if (canceled) return;
          applyLoadedResult(scan, scan.reportId ?? null);
        } catch {
          try {
            const report = await getReport(scanRouteId, getToken);
            if (canceled) return;
            applyLoadedResult(resultFromReport(report), report.id);
          } catch (err) {
            if (canceled) return;
            setCurrentResult(null);
            setLoadedReport(null);
            setError(err instanceof Error ? err.message : 'Failed to load scan result');
          }
        } finally {
          if (!canceled) {
            setIsRouteLoading(false);
          }
        }
      }
    };

    loadScanResult();

    return () => {
      canceled = true;
      disposeWatch?.();
    };
  }, [scanRouteId, currentResult?.id, currentResult?.licenseStatus, isLoading, authEnabled, isSignedIn, getToken, applyLoadedResult]);

  useEffect(() => {
    if (!reportRouteId) return;
    if (authEnabled && !isSignedIn) return;

    let canceled = false;

    const loadReportResult = async () => {
      // A scan is actively starting (e.g. "Run full audit" from a locked report):
      // don't clobber the in-progress state by re-fetching the old route's report.
      if (isLoading) return;
      if (isRouteResultFresh(currentResult?.id, currentResult?.licenseStatus, reportRouteId)) return;

      setIsRouteLoading(true);
      setError(null);

      try {
        const report = await getReport(reportRouteId, getToken);
        if (canceled) return;
        applyLoadedResult(resultFromReport(report), report.id);
      } catch (err) {
        if (canceled) return;
        setCurrentResult(null);
        setLoadedReport(null);
        setError(err instanceof Error ? err.message : 'Failed to load report');
      } finally {
        if (!canceled) {
          setIsRouteLoading(false);
        }
      }
    };

    loadReportResult();

    return () => {
      canceled = true;
    };
  }, [reportRouteId, currentResult?.id, currentResult?.licenseStatus, isLoading, authEnabled, isSignedIn, getToken, applyLoadedResult]);

  const fetchPastScans = useCallback(async () => {
    if (authEnabled && !isSignedIn) {
      setPastScans([]);
      return;
    }
    try {
      const data = await getScans(getToken);
      setPastScans(data.scans);
    } catch {
      // API might not be running
    } finally {
      setPastScansLoaded(true);
    }
  }, [authEnabled, isSignedIn, getToken]);

  useEffect(() => {
    fetchPastScans();
  }, [fetchPastScans]);

  const handleScan = useCallback(async (
    urls: string[],
    options: { sections: ScanSectionOptions; headless: boolean }
  ) => {
    setHashRoute('#/scanner');
    setIsLoading(true);
    setError(null);
    setCreditError(null);
    setCurrentResult(null);
    setProgress(null);
    setPurchasedScanId(null);
    setLoadedReport(null);
    setBatchInfo(urls.length > 1 ? { current: 1, total: urls.length, url: urls[0] } : null);
    setScanningUrl(urls[0]);

    const batchResults: ScanResult[] = [];
    const batchErrors: string[] = [];

    for (let i = 0; i < urls.length; i++) {
      const scanUrl = urls[i];
      const startTimeScan = Date.now();
      trackScanStarted(scanUrl);

      if (urls.length > 1) {
        setBatchInfo({ current: i + 1, total: urls.length, url: scanUrl });
      }
      setScanningUrl(scanUrl);
      setProgress(null);
      try {
        const onProgress = (prog: ScanProgressType) => {
          setProgress((prev) => mergeProgressFrame(prev, prog));
        };
        const result = await startScan(scanUrl, options, onProgress, getToken);
        batchResults.push(result);
        trackScanComplete(scanUrl, result, Date.now() - startTimeScan);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'An error occurred during the scan';
        trackScanError(scanUrl, msg, 'dashboard_scan');
        if (isInsufficientCredits(err)) {
          setCreditError(err);
        } else {
          batchErrors.push(`${scanUrl}: ${msg}`);
        }
      }
    }

    if (batchErrors.length > 0) {
      setError(batchErrors.join('\n'));
    }

    setBatchInfo(null);
    setScanningUrl(null);
    if (batchResults.length > 0) {
      setProgress({ stage: 'complete', message: 'Scan complete!', percentage: 100 });
      if (batchResults.length === 1) {
        setAllBatchResults([]);
        setCurrentResult(batchResults[0]);
        setHashRoute(`#/scan/${encodeURIComponent(batchResults[0].id)}`);
      } else {
        setAllBatchResults(batchResults);
        setBatchResultIndex(0);
        setCurrentResult(batchResults[0]);
        setHashRoute(`#/scan/${encodeURIComponent(batchResults[0].id)}`);
      }
    }
    fetchPastScans();
    refetchCreditInfo();
    setIsLoading(false);
  }, [setHashRoute, getToken, fetchPastScans, refetchCreditInfo]);

  // #1762: a report unlocked in place keeps its id, and the route effects
  // only reload on an id change, so reload it here through the same reads
  // the scan route uses. On failure the report stays as shown; a page
  // reload picks up the unlock.
  const currentResultId = currentResult?.id;
  const handleReportUnlocked = useCallback(async () => {
    refetchCreditInfo();
    if (!currentResultId) return;
    try {
      const scan = await getScan(currentResultId, getToken);
      applyLoadedResult(scan, scan.reportId ?? currentReportId);
    } catch {
      if (!currentReportId) return;
      try {
        const report = await getReport(currentReportId, getToken);
        applyLoadedResult(resultFromReport(report), report.id);
      } catch { /* leave the locked view; a reload shows the unlock */ }
    }
  }, [currentResultId, currentReportId, getToken, applyLoadedResult, refetchCreditInfo]);

  // #1776: back from the unlock checkout. Show the report once the webhook
  // has unlocked it, with the "Report unlocked" banner, then drop the
  // session id from the hash so a reload does not wait again.
  const loadUnlockReturn = useCallback((id: string) => getScan(id, getToken), [getToken]);
  const handleUnlockReturned = useCallback((scan: ScanResult) => {
    applyLoadedResult(scan, scan.reportId ?? null);
    setPurchasedScanId(scan.id);
    setPurchaseUnlockedReport(true);
    refetchCreditInfo();
    setHashRoute(`#/scan/${encodeURIComponent(scan.id)}`);
  }, [applyLoadedResult, refetchCreditInfo, setHashRoute]);
  const unlockReturn = useUnlockReturn({
    sessionId: unlockSessionId,
    scanId: scanRouteId,
    enabled: !authEnabled || !!isSignedIn,
    load: loadUnlockReturn,
    onUnlocked: handleUnlockReturned,
  });

  // The "Run full audit · 1 credit" button.
  const handleRunFullAudit = useCallback((url: string) => {
    return handleScan([url], {
      sections: {
        uiCompliance: true,
        trackerDetection: true,
        gpcValidation: true,
        privacyPolicy: true,
      },
      headless: true,
    });
  }, [handleScan]);

  // #1779: a claimed full audit (#1777) already ran every check, so it is
  // never scanned again. The account now owns the locked report: show it on
  // #/scan/:id and, when the visitor clicked Unlock before signing up (the
  // unlock intent, for this scan only), start the unlock checkout (#1776).
  // An account that can unlock without paying (a credit, or unlimited) gets
  // the in-place offer instead, and so does one whose balance cannot be read:
  // Stripe is never opened on a guess. Reaching here at all means the claim
  // resolved first.
  const settleFullClaim = useCallback(async (scan: ScanResult) => {
    setError(null);
    applyLoadedResult(scan, scan.reportId ?? null);
    setHashRoute(`#/scan/${encodeURIComponent(scan.id)}`);

    if (getUnlockIntent() !== scan.id || !scan.reportId) return;
    clearUnlockIntent();
    const info = creditInfoRef.current.resolved
      ? creditInfoRef.current
      : await getCreditInfo().catch(() => null);
    if (!info || info.balance !== 0) return;
    try {
      trackInitiateCheckout({ reportId: scan.reportId, plan: 'unlock', value: 49 });
      const meta = readMetaAttribution();
      const { url } = await (meta ? createUnlockCheckout(scan.reportId, meta) : createUnlockCheckout(scan.reportId));
      billingRedirect.assign(url);
    } catch (err) {
      // Unlocked since the claim (another tab, monitoring): show it. Any
      // other failure leaves the locked report and its own Unlock button.
      if (err instanceof ApiError && err.apiCode === 'REPORT_ALREADY_UNLOCKED') {
        try {
          const fresh = await getScan(scan.id, getToken);
          applyLoadedResult(fresh, fresh.reportId ?? scan.reportId);
        } catch { /* a reload shows the unlock */ }
      }
    }
  }, [applyLoadedResult, setHashRoute, getToken]);

  // After a successful claim (useClaimScan has already moved to #/scan/:id):
  // a full audit (#1779) settles through settleFullClaim. A lite claim (the
  // GPC-only fallback, a /tools scan, or a lane that could not be read)
  // starts nothing since the #1782 cutover; its report's own offer leads to
  // the full audit. A failed read leaves the visitor on the claimed report.
  const settleClaim = useCallback(async (scanId: string) => {
    try {
      const scan = await getScan(scanId, getToken);
      if (await readReportKind(scan.reportId) === 'full') await settleFullClaim(scan);
    } catch { /* the claimed report stays as shown */ }
  }, [getToken, settleFullClaim]);

  // #1779: SCAN_ALREADY_CLAIMED. Only an authenticated read of the scan
  // proves this account owns it (a second tab, a reload after the claim);
  // then a full audit settles as if this claim had succeeded. Anything else
  // drops the unlock intent. A lite claim never auto-starts from here.
  const resumeAlreadyClaimed = useCallback(async (scanId: string) => {
    try {
      const scan = await getScan(scanId, getToken);
      if (await readReportKind(scan.reportId) === 'full') {
        await settleFullClaim(scan);
        return;
      }
    } catch { /* not this account's scan */ }
    clearUnlockIntent();
  }, [getToken, settleFullClaim]);

  useEffect(() => {
    settleClaimRef.current = settleClaim;
    resumeAlreadyClaimedRef.current = resumeAlreadyClaimed;
  }, [settleClaim, resumeAlreadyClaimed]);

  // Issue #870: a signed-out visitor lands on #/scan/:id straight from the
  // www lite-scan redirect. The scan is always created `locked`, so without
  // this check they'd wait through the loading interstitial only to see the
  // bare "Not Tested" locked report. resolveClaimIdentity() covers both the
  // redirect's URL params and the cross-subdomain cookie fallback (e.g. the
  // sign-up email-verification redirect, which returns to a clean URL).
  const claimIdentity = resolveClaimIdentity({
    urlScanId: claimParams.scanId,
    urlToken: claimParams.intentToken,
    persistedScanId: getScanIntentId(),
    persistedToken: getScanIntentToken(),
  });
  const isAnonymousClaimHandoff =
    authEnabled && !isSignedIn && !!scanRouteId && claimIdentity.scanId === scanRouteId;
  // #1779: which lane produced the token holder's scan. A full audit (#1777)
  // lands on its locked report; a lite scan keeps the claim landing until the
  // cutover (#1782). Read once the masked result for this route has landed.
  const handoffResult = currentResult?.id === scanRouteId ? currentResult : null;
  const handoffReportId = isAnonymousClaimHandoff && handoffResult?.licenseStatus === 'locked'
    ? handoffResult.reportId ?? null
    : null;
  const [handoffKind, setHandoffKind] = useState<{ reportId: string; kind: ReportKind | null } | null>(null);
  useEffect(() => {
    if (!handoffReportId) return;
    let canceled = false;
    readReportKind(handoffReportId).then((kind) => {
      if (!canceled) setHandoffKind({ reportId: handoffReportId, kind });
    });
    return () => { canceled = true; };
  }, [handoffReportId]);
  const handoffKindPending = !!handoffReportId && handoffKind?.reportId !== handoffReportId;
  const showLockedFullAudit = !!handoffReportId && !handoffKindPending && handoffKind?.kind === 'full';
  const claimToken = claimIdentity.token;
  const signedOutUnlock = useMemo<SignedOutUnlock | undefined>(() => {
    if (!showLockedFullAudit || !scanRouteId) return undefined;
    const persistClaimCookies = () => {
      if (claimToken) {
        setScanIntentId(scanRouteId);
        setScanIntentToken(claimToken);
      }
    };
    return {
      returnHash: `#/scan/${encodeURIComponent(scanRouteId)}`,
      // Pay first, account after (2026-10-27): the claim token opens checkout.
      intentToken: claimToken,
      onBeforeCheckout: persistClaimCookies,
      onKeepSignedOut: persistClaimCookies,
      onUnlockSignedOut: () => {
        setUnlockIntent(scanRouteId);
        // The claim after sign-up reads these when the redirect drops the
        // URL params; the www redirect normally set them already.
        persistClaimCookies();
      },
    };
  }, [showLockedFullAudit, scanRouteId, claimToken]);

  // The three standalone routes (#/sales/:id, #/policy-analyzer/:id,
  // #/payment-success) live in one component (issue #416) — it returns null
  // for every other state, which falls through to the rest of App().
  const standalonePage = renderStandalonePagesRoute({
    salesReportId,
    salesReportToken,
    policyAnalyzerId,
    paymentSessionId,
    paymentMode,
    paymentReportId,
    isSignedIn,
    authEnabled,
    userId: user?.id,
    creditInfo,
    onPolicyAnalyzerBack: () => setHashRoute('#/scanner'),
    onPaymentNavigate: (route, outcome) => {
      // Extract scanId from route like '#/scan/{scanId}?payment=success'.
      // Only a purchase outcome earns the post-purchase banner; the way back
      // from a monitoring checkout reports none.
      const match = outcome ? route.match(/^#\/scan\/([^?]+)/) : null;
      setPurchasedScanId(match ? decodeURIComponent(match[1]) : null);
      setPurchaseUnlockedReport(!!match && !!outcome?.reportUnlocked);
      // The purchase moved the balance (or the plan): the header and the
      // report's offer must not keep the pre-checkout numbers (#1776).
      refetchCreditInfo();
      setHashRoute(route);
    },
  });
  if (standalonePage) {
    return standalonePage;
  }

  // The URL's ?domain= param covers the primary redirect path; the cookie
  // fallback path has no such param, so once the background poll's result
  // for this exact route lands, fall back to deriving it from the scanned URL.
  const handoffDomain =
    claimParams.domain ??
    (currentResult?.id === scanRouteId ? hostnameFromUrl(currentResult?.url) : null);

  if (isAnonymousClaimHandoff && !showLockedFullAudit) {
    // Until the lane is known there is nothing honest to pitch: a running
    // scan shows its progress, and a landed one waits for its summary.
    if ((isRouteLoading && !handoffResult) || handoffKindPending) {
      return renderRouteLoadingRoute({
        scanRouteId,
        reportRouteId,
        isRouteLoading: true,
        hasCurrentResult: false,
        error,
        routeProgress,
        navigateTo,
        queueCount,
        authEnabled,
        creditInfo,
        onBillingClick: () => setHashRoute('#/billing'),
      });
    }
    return (
      <AnonymousClaimHandoff
        scanRouteId={scanRouteId as string}
        claimParams={claimParams}
        domain={handoffDomain}
        maskedResult={currentResult?.id === scanRouteId ? currentResult : null}
        isClaiming={isClaiming}
        claimResult={claimResult}
        authEnabled={authEnabled}
        onDismiss={() => {
          setClaimResult(null);
          clearClaimParams();
          // A genuine dismiss abandons the claim — clear the persisted
          // cookie identity too, or the cookie fallback above would
          // immediately re-match this same scan route and undo the dismiss.
          clearScanIntentId();
          clearScanIntentToken();
          // "Scan a different site" means starting over: drop the polled
          // locked result too, or the scanner home would render the
          // abandoned scan's report view instead of a fresh scan form.
          setCurrentResult(null);
          setLoadedReport(null);
          // Signed out, there is no scanner home to return to: a new scan
          // starts on the marketing site's audit form.
          if (authEnabled && !isSignedIn) {
            navigation.replace(freeAuditUrl());
            return;
          }
          setHashRoute('#/scanner');
        }}
        navigateTo={navigateTo}
        queueCount={queueCount}
        creditInfo={creditInfo}
        onBillingClick={() => setHashRoute('#/billing')}
      />
    );
  }

  const routeLoadingPage = renderRouteLoadingRoute({
    scanRouteId,
    reportRouteId,
    isRouteLoading,
    hasCurrentResult: !!currentResult,
    error,
    routeProgress,
    navigateTo,
    queueCount,
    authEnabled,
    creditInfo,
    onBillingClick: () => setHashRoute('#/billing'),
  });
  if (routeLoadingPage) {
    return routeLoadingPage;
  }

  if (currentResult) {
    const handleBatchBack = () => {
      setCurrentResult(null);
      setAllBatchResults([]);
      setBatchResultIndex(0);
      setShowPaymentBanner(false);
      setHashRoute('#/scanner');
    };
    const handleBatchNav = (newIndex: number) => {
      setBatchResultIndex(newIndex);
      setCurrentResult(allBatchResults[newIndex]);
      setHashRoute(`#/scan/${encodeURIComponent(allBatchResults[newIndex].id)}`);
    };

    return (
      <ScanResultRoute
        result={currentResult}
        allBatchResults={allBatchResults}
        batchResultIndex={batchResultIndex}
        showPaymentBanner={showPaymentBanner}
        purchasedScanId={purchasedScanId}
        purchaseUnlockedReport={purchaseUnlockedReport}
        unlockReturn={unlockSessionId ? unlockReturn : undefined}
        reportId={currentReportId}
        userId={user?.id}
        isSignedIn={isSignedIn}
        authEnabled={authEnabled}
        queueCount={queueCount}
        creditInfo={creditInfo}
        onBillingClick={() => setHashRoute('#/billing')}
        onNavigate={(page) => {
          setCurrentResult(null);
          setAllBatchResults([]);
          setBatchResultIndex(0);
          setShowPaymentBanner(false);
          navigateTo(page);
        }}
        onRunFullAudit={handleRunFullAudit}
        onUnlocked={handleReportUnlocked}
        signedOutUnlock={signedOutUnlock}
        onBack={handleBatchBack}
        onBatchNav={handleBatchNav}
        onDismissPaymentBanner={() => setShowPaymentBanner(false)}
        onDismissPurchasedBanner={() => setPurchasedScanId(null)}
      />
    );
  }

  // The dashboard keeps no signed-out start page: a signed-out visitor on its
  // own pages signs in on the marketing site and comes back. The public
  // routes already returned above (standalone pages, the anonymous claim
  // handoff), and a #/scan/:id or #/report/:id route, a claim landing (URL
  // params or the cookie fallback) and the post-payment screen stay here.
  // Read off the live hash, not route state: this render can run before the
  // hash effect has applied the route, and a redirect from a #/scan/:id the
  // state has not caught up with would strand a token holder.
  const liveRoute = resolveRoute(window.location.hash).patch;
  const isDashboardPage =
    liveRoute.currentPage === 'scanner' || liveRoute.currentPage === 'reports' ||
    liveRoute.currentPage === 'queue' || liveRoute.currentPage === 'billing' ||
    liveRoute.currentPage === 'monitoring';
  if (
    authEnabled &&
    !isSignedIn &&
    isDashboardPage &&
    !liveRoute.scanRouteId &&
    !liveRoute.reportRouteId &&
    !claimIdentity.scanId
  ) {
    return <SignedOutRedirect />;
  }

  // The six AuthGatedPage-wrapped routes (#/queue, #/billing, #/reports, and
  // the three /admin/* routes) live in one component (issue #416) — it
  // returns null for every other currentPage (including 'admin-user-detail'
  // with no id yet), which falls through to the scanner homepage below.
  const secondaryPage = renderSecondaryPagesRoute({
    currentPage,
    adminUserId,
    navigateTo,
    queueCount,
    authEnabled,
    creditInfo,
    onBillingClick: () => setHashRoute('#/billing'),
    isSignedIn,
    isAdmin,
    getToken,
    setHashRoute,
  });
  if (secondaryPage) {
    return secondaryPage;
  }

  // Scanner homepage
  return (
    <div style={{ minHeight: '100dvh', backgroundColor: T.gray50, display: 'flex', flexDirection: 'column' }}>
      <Header
        currentPage="scanner"
        onNavigate={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditBalance={creditInfo.balance}
        creditTier={creditInfo.tier}
        creditLoading={creditInfo.loading}
        creditHistory={creditInfo.hasCreditHistory}
        onBillingClick={() => setHashRoute('#/billing')}
      />

      <main style={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column' }} className="scanner-home">
      {/* Full-bleed dark audit band */}
      <HeroSection
        authEnabled={authEnabled}
        isLoading={isLoading}
        handleScan={handleScan}
        creditInfo={creditInfo}
        claimParams={claimParams}
        isClaiming={isClaiming}
        claimResult={claimResult}
        setClaimResult={setClaimResult}
        clearClaimParams={clearClaimParams}
        showPaymentBanner={showPaymentBanner}
        scanRouteId={scanRouteId}
        isSignedIn={isSignedIn}
        prefill={auditPrefill}
      />
      <ScannerDashboardContent
        isLoading={isLoading}
        progress={progress}
        batchInfo={batchInfo}
        scanningUrl={scanningUrl}
        creditError={creditError}
        error={error}
        claimScanId={claimParams.scanId}
        pastScans={pastScans}
        pastScansLoaded={pastScansLoaded}
        authEnabled={authEnabled}
        isSignedIn={isSignedIn}
        userId={user?.id}
        packPrice={creditInfo.packPrice}
        creditsPerPurchase={creditInfo.creditsPerPurchase}
        onViewScan={(scan) => {
          setCurrentResult(scan);
          setAllBatchResults([]);
          setBatchResultIndex(0);
          setHashRoute(`#/scan/${encodeURIComponent(scan.id)}`);
        }}
        onAuditAgain={creditInfo.balance !== null && creditInfo.balance <= 0 ? undefined : (url) => {
          setAuditPrefill((prev) => ({ url, nonce: (prev?.nonce ?? 0) + 1 }));
          try {
            const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
          } catch {
            // jsdom and very old browsers: the form still takes focus.
          }
        }}
      />
      </main>

    </div>
  );
}

export default App;
